import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { useLaunchGate } from "@/lib/useLaunchGate";
import LaunchGatePage from "@/pages/LaunchGatePage";
import ComingSoonPopup from "@/components/ComingSoonPopup";

function isAdminPath(pathname) {
  return pathname === "/admin" || pathname.startsWith("/admin/");
}

function isHomePath(pathname) {
  return pathname === "/";
}

/**
 * Full-site gate when enabled; optional dismissible Coming Soon popup on homepage only.
 */
export default function LaunchGateGuard({ children }) {
  const { pathname } = useLocation();
  const { enabled, popupEnabled, message, launchAt, loading } = useLaunchGate();
  const [popupDismissed, setPopupDismissed] = useState(false);
  const admin = isAdminPath(pathname);

  useEffect(() => {
    if (!isHomePath(pathname)) {
      setPopupDismissed(false);
    }
  }, [pathname]);

  // Admin UI must never wait on the public gate. After the first cached
  // response, keep the tree mounted so a refetch cannot unmount the app.
  if (loading && !admin) {
    return (
      <div
        className="min-h-screen grid place-items-center bg-background text-white/70 text-[14px]"
        data-testid="launch-gate-loading"
      >
        Loading…
      </div>
    );
  }

  if (enabled && !admin) {
    return <LaunchGatePage message={message} launchAt={launchAt} />;
  }

  const showPopup =
    popupEnabled && isHomePath(pathname) && !isAdminPath(pathname) && !popupDismissed;

  return (
    <>
      {children}
      {showPopup && (
        <ComingSoonPopup
          message={message}
          launchAt={launchAt}
          onDismiss={() => setPopupDismissed(true)}
        />
      )}
    </>
  );
}
